import { Fragment, useEffect, useState, useCallback, useRef } from 'react'
import { Collapse, Box, Paper } from '@mui/material'
import { useTranslation } from 'react-i18next'
import attributes from './attributes'
import { CollapsibleFilterPanel } from 'packages/eid-ui'
import {
    useIsSmallScreenFalse,
    useQuery,
    useSessionStorage,
} from 'packages/core'
import { useHistory } from 'react-router'
import { InboxTable, InboxTableHead, InboxRow } from './ViewAllTableComponent'
import {
    AppLayout,
    useEidGrid,
    CardViewLoader,
    Filters,
    DesktopContainer,
    GridContainer,
    FiltersColumn,
    FilterContainer,
    ListingGrid,
} from 'components'
import Breadcrumbs from 'components/BreadCrumbs'

import { useApplicationSettings, useUploadCSVInbox } from 'hooks'
import { CsvInboxDetail } from 'containers/ViewAllCsvInbox/CsvInboxDetail'
import { PageContextProvider, usePageContext } from 'pageContext'
import { useCSVInboxItems } from 'hooks'
import useSubcomponents from 'useSubcomponents'

import { ITEMS_VIEW, showSearchToLoadMessage } from 'utils'

import InfoBoxMessage from 'packages/eid-ui/InfoBoxMessage'
import MassUpload from 'components/Popups/MassUpload'

const canSeeMassUpload = 'RTA-CSVInbox-MassUpload-Control'

const ViewAllLayout = ({ _filters }) => {
    const { getAccessBasedAttributes, hasAccessToControl } = useSubcomponents()

    const history = useHistory()

    const {
        location: { pathname },
    } = history

    // TODO: Hack to enable devfeature

    const filterContainer = useRef(null)
    const { t } = useTranslation()
    const query = useQuery()
    const [state, dispatch] = usePageContext()

    const filters = _filters.map((f) => f)

    const {
        list,
        totalCount,
        loading: isLoading,
        loadingMore: isLoadingMore,
        pagination,
        refetch,
        rest,
    } = useCSVInboxItems()
    const { mutateAsync: uploadCSVInbox, isLoading: isUploading } =
        useUploadCSVInbox()
    const [windowSize, setWindowSize] = useState(window.innerWidth)
    const [filterWidth, setFilterWidth] = useState(0)

    const [openFilters, setOpenFilters] = useSessionStorage(
        'FILTERS_EXPANDED',
        true,
    )
    const handleWindowResize = useCallback(() => {
        setWindowSize(window.innerWidth)
    }, [])

    useEffect(() => {
        window.addEventListener('resize', handleWindowResize)

        return () => {
            window.removeEventListener('resize', handleWindowResize)
        }
    }, [handleWindowResize])

    useEffect(() => {
        setFilterWidth(filterContainer?.current?.offsetWidth)
    }, [openFilters, windowSize])

    useEffect(() => {
        if (rest && state.shouldLoadTags) {
            dispatch({
                type: 'SET_TAGS',
                payload: rest.tags,
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rest])

    const isSmallScreen = useIsSmallScreenFalse()

    const { viewSwitcher, setCurrentView, viewToRender } = useEidGrid({
        isLoading,
        data: list || [],
        isLoadingMore,
        noDataMessage: t('NoDataMessage'),
        pagination,
        attributes: getAccessBasedAttributes(attributes),

        tableViewConfig: {
            TableComponent: InboxTable,
            TableHeadComponent: InboxTableHead,
            TableRowComponent: InboxRow,
        },
    })

    useEffect(() => {
        if (isSmallScreen) {
            setCurrentView(ITEMS_VIEW.CARD)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSmallScreen])

    const csvInboxId = query.get('id')
    const showDetailsPage = csvInboxId

    const { data: appConfig } = useApplicationSettings()
    const { staticWorkflows } = appConfig

    let detailContent = null

    const handleUpload = async (formData) => {
        const response = await uploadCSVInbox(formData)
        if (response?.data?.isSuccess) {
            refetch()
        }
        return response
    }
    if (csvInboxId) {
        detailContent = (
            <>
                <DesktopContainer>
                    <Box paddingRight={'8px'} marginBottom="16px" width="100%">
                        <PageContextProvider key="RequestViewCsvInboxDetail">
                            <CsvInboxDetail csvInboxId={csvInboxId} />
                        </PageContextProvider>
                    </Box>
                </DesktopContainer>
            </>
        )
    }

    let listingContent
    listingContent = (
        <>
            {/* Breadcrumbs for listing page - above main content */}
            {!showDetailsPage && (
                <Box
                    sx={{
                        marginTop: '16px',
                        paddingLeft: '8px',
                    }}
                >
                    <Breadcrumbs
                        breadcrumbs={[
                            {
                                labelKey: 'All',
                                disabled: true,
                            },
                            {
                                labelKey: 'CSVInbox',
                                disabled: true,
                            },
                        ]}
                    />
                </Box>
            )}
            {
                <DesktopContainer sx={{ paddingTop: '1rem !important' }}>
                    <Fragment>
                        {filters && filters.length > 0 && (
                            <FiltersColumn ref={filterContainer}>
                                <CollapsibleFilterPanel
                                    openFilters={openFilters}
                                    setOpenFilters={(e) =>
                                        setOpenFilters((prev) => !prev)
                                    }
                                />

                                <Collapse
                                    in={openFilters}
                                    timeout={300}
                                    unmountOnExit
                                >
                                    <>
                                        {filters.map((f) => {
                                            const FilterToRender =
                                                Filters.filtersMap[f.name]
                                            return (
                                                <FilterContainer
                                                    data-protectedsubcomponent={
                                                        f.requireAccess
                                                    }
                                                    key={f.name}
                                                >
                                                    <FilterToRender
                                                        config={f.config}
                                                    />
                                                </FilterContainer>
                                            )
                                        })}
                                    </>
                                </Collapse>
                            </FiltersColumn>
                        )}

                        <ListingGrid>
                            <>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'end',
                                        alignItems: 'center',
                                        gap: '16px'
                                    }}
                                >
                                    <Filters.TextSearch
                                        variant="widthSm"
                                        searchInfoMessage={
                                            showSearchToLoadMessage(
                                                pathname,
                                                state?.searchTerm,
                                                state?.isEnterPressed,
                                            )
                                                ? 'PressEnterOrMinSearchCharLimit'
                                                : 'Min_Search_Char_Limit'
                                        }
                                        onEnterPress={() => {
                                            dispatch({
                                                type: 'SET_MULTIPLE_PROPS',
                                                payload: {
                                                    isEnterPressed: true,
                                                    searchTerm: '',
                                                },
                                            })
                                        }}
                                    />
                                    {hasAccessToControl(canSeeMassUpload) ? (
                                        <Box
                                            data-protectedsubcomponent={
                                                canSeeMassUpload
                                            }
                                        >
                                            <MassUpload
                                                btnLabelKey="MassUpload"
                                                headerLabelKey="Upload"
                                                onUpload={(formData) =>
                                                    handleUpload(formData)
                                                }
                                                isLoading={isUploading}
                                            />
                                        </Box>
                                    ) : (
                                        <Box></Box>
                                    )}
                                    
                                </Box>
                                <GridContainer
                                    filterWidth={filterWidth}
                                    openFilters={openFilters}
                                >
                                    {!showSearchToLoadMessage(
                                        pathname,
                                        state?.searchTerm,
                                        state?.isEnterPressed,
                                    ) ? (
                                        viewToRender
                                    ) : (
                                        <Box
                                            sx={{
                                                margin: 'auto',
                                                display: 'table',
                                                marginTop: '10rem',
                                            }}
                                        >
                                            <InfoBoxMessage
                                                message={t('SearchToLoad')}
                                            />
                                        </Box>
                                    )}
                                </GridContainer>
                            </>
                        </ListingGrid>
                    </Fragment>
                </DesktopContainer>
            }
        </>
    )

    return (
        <AppLayout
            showSubHeader={false}
            totalCount={totalCount}
            viewSwitcher={viewSwitcher}
            showNavTabs={!showDetailsPage}
            showBackButton={showDetailsPage}
        >
            <Fragment>
                {showDetailsPage ? detailContent : listingContent}
                <></>
            </Fragment>
        </AppLayout>
    )
}

export default ViewAllLayout
